import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { canManageForms } from './roles';
import { groupSubmissionsByCompany, groupSubmissionsByUser } from './submissionGrouping';
import { formatSubmittedAt } from './dateFormat';
import { CARD, PRIMARY_BTN } from './ui';
import { API_BASE_URL } from './config';
import FlameIcon from './FlameIcon';

const plural = (count, word, plural_) =>
  `${count} ${count === 1 ? word : plural_ || `${word}s`}`;

// Left padding per nesting level. A section's own header sits at INDENT[depth]
// and whatever it contains one step further in, so the tree still reads as a
// tree once a dev's company level pushes everything down a rung. Each step is
// tighter on a phone, where four levels of desktop indent would leave the
// deepest rows a sliver wide.
const INDENT = ['pl-4 sm:pl-6', 'pl-8 sm:pl-12', 'pl-12 sm:pl-[4.5rem]'];

const Chevron = ({ open, className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`${className} flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
      clipRule="evenodd"
    />
  </svg>
);

// Every level of the tree is the same control: a full-width header that opens
// and closes what sits under it.
const SectionHeader = ({ open, onToggle, title, meta, className }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className={`w-full flex items-center justify-between gap-3 text-left py-3 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-400 ${className}`}
  >
    <span className="flex items-center gap-2 min-w-0">
      <Chevron open={open} />
      <span className="truncate font-semibold" title={title}>
        {title}
      </span>
    </span>
    <span className="text-xs text-gray-400 flex-shrink-0">{meta}</span>
  </button>
);

const SubmissionRow = ({ chain, open, onToggle, onOpen, indent }) => {
  const { root, resubmissions } = chain;
  // Rows are ordered by the original's date, so a chain updated later says so
  // rather than looking stale.
  const newest = resubmissions[0];

  return (
    <li className={`pr-4 sm:pr-6 py-3 ${indent}`}>
      {/* The re-submission toggle drops below the title on a narrow screen
          instead of squeezing it down to an ellipsis. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <button
          type="button"
          onClick={() => onOpen(root.id)}
          className="flex-1 min-w-0 text-left bg-transparent p-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 group"
        >
          <span className="block text-base font-medium text-white group-hover:text-purple-300 transition-colors duration-200 truncate">
            {root.title || root.form_name || `Submission #${root.id}`}
          </span>
          <span className="block text-xs text-gray-500 mt-0.5">
            Submitted {formatSubmittedAt(root.submitted_at, root.submitted_tz)}
            {newest && (
              <>
                {' · updated '}
                {formatSubmittedAt(newest.submitted_at, newest.submitted_tz)}
              </>
            )}
          </span>
        </button>
        {resubmissions.length > 0 && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="flex items-center gap-1.5 self-start sm:self-auto flex-shrink-0 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors duration-200 border border-amber-500/30"
          >
            <Chevron open={open} />
            {plural(resubmissions.length, 're-submission')}
          </button>
        )}
      </div>

      {open && resubmissions.length > 0 && (
        <ul className="mt-2 ml-1 border-l border-gray-700 pl-4 space-y-1">
          {resubmissions.map((sub) => (
            <li key={sub.id}>
              <button
                type="button"
                onClick={() => onOpen(sub.id)}
                className="w-full text-left bg-transparent px-2 py-1.5 rounded-md hover:bg-gray-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 transition-colors duration-200"
              >
                <span className="block text-sm text-gray-200 truncate">
                  {sub.title || `Submission #${sub.id}`}
                </span>
                <span className="block text-xs text-gray-500">
                  {formatSubmittedAt(sub.submitted_at, sub.submitted_tz)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const UserSection = ({ group, depth, isOpen, toggle, onOpen }) => {
  const key = `user-${group.key}`;
  // Submitters are the top level for managers and admins, so they start open;
  // under a dev's company sections they are one level down, so they start shut.
  const defaultOpen = depth === 0;
  const open = isOpen(key, defaultOpen);

  return (
    <li>
      <SectionHeader
        open={open}
        onToggle={() => toggle(key, defaultOpen)}
        title={group.email}
        meta={plural(group.submissionCount, 'submission')}
        className={`text-sm text-white pr-4 sm:pr-6 hover:bg-gray-700/40 ${INDENT[depth]} ${
          depth === 0 ? 'bg-gray-900/40' : 'bg-gray-900/20'
        }`}
      />
      {open && (
        <ul className="divide-y divide-gray-700/60">
          {group.chains.map((chain) => (
            <SubmissionRow
              key={chain.root.id}
              chain={chain}
              indent={INDENT[depth + 1]}
              open={isOpen(`chain-${chain.root.id}`, false)}
              onToggle={() => toggle(`chain-${chain.root.id}`, false)}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const CompanySection = ({ company, isOpen, toggle, onOpen }) => {
  // Companies are a dev's top level, so they start open.
  const open = isOpen(company.key, true);

  return (
    <li>
      <SectionHeader
        open={open}
        onToggle={() => toggle(company.key, true)}
        title={company.companyName}
        meta={`${plural(company.users.length, 'person', 'people')} · ${plural(
          company.submissionCount,
          'submission'
        )}`}
        className={`text-base text-white pr-4 sm:pr-6 bg-violet-500/10 hover:bg-violet-500/20 ${INDENT[0]}`}
      />
      {open && (
        <ul className="divide-y divide-gray-700/60">
          {company.users.map((group) => (
            <UserSection
              key={group.key}
              group={group}
              depth={1}
              isOpen={isOpen}
              toggle={toggle}
              onOpen={onOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// One roster column of the coverage panel (submitted / not yet submitted).
const RosterList = ({ title, users, accent, emptyText }) => (
  <div className="border-t border-gray-700">
    <div className="px-5 py-3 flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accent}`}>
        {users.length}
      </span>
    </div>
    {users.length === 0 ? (
      <p className="px-5 pb-4 text-xs text-gray-500">{emptyText}</p>
    ) : (
      <ul className="pb-2">
        {users.map((u) => (
          // A guest whose one-time account has been consumed has no id left;
          // their address is what the row is, and it is unique either way.
          <li key={u.id ?? u.email} className="px-5 py-1.5 flex items-center justify-between gap-2">
            <span className="text-sm text-gray-300 truncate" title={u.email}>
              {u.email}
            </span>
            {u.submission_count > 1 && (
              <span className="text-xs text-gray-500 flex-shrink-0">×{u.submission_count}</span>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const CoverageSection = ({ section, collapsible, isOpen, toggle }) => {
  const key = `coverage-${section.company_id ?? 'none'}`;
  const open = isOpen(key, true);
  const percent = section.total > 0 ? Math.round((section.submitted_count / section.total) * 100) : 0;

  return (
    <div className="border-t border-gray-700 first:border-t-0">
      {collapsible ? (
        <SectionHeader
          open={open}
          onToggle={() => toggle(key, true)}
          title={section.company_name || 'No company'}
          meta={`${section.submitted_count}/${section.total}`}
          className="text-sm text-white bg-violet-500/10 hover:bg-violet-500/20 px-5"
        />
      ) : null}

      {open && (
        <>
          <div className="px-5 py-4">
            <p className="text-sm text-gray-400">
              <span className="text-emerald-300 font-semibold">
                {section.submitted_count} submitted
              </span>
              <span className="text-gray-600"> / </span>
              <span className="text-amber-300 font-semibold">
                {section.not_submitted_count} not yet
              </span>
              <span className="text-gray-500"> · {plural(section.total, 'person', 'people')}</span>
            </p>
            <div className="mt-3 h-1.5 w-full bg-gray-700/70 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-violet-400 rounded-full transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
          <RosterList
            title="Submitted"
            users={section.submitted}
            accent="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            emptyText="Nobody here has submitted this survey yet."
          />
          <RosterList
            title="Not yet submitted"
            users={section.not_submitted}
            accent="bg-amber-500/15 text-amber-300 border border-amber-500/30"
            emptyText="Everyone here has submitted this survey."
          />
        </>
      )}
    </div>
  );
};

const COVERAGE_TITLES = { team: 'Team coverage', company: 'Company coverage', all: 'Coverage by company' };

const CoveragePanel = ({ coverage, isOpen, toggle }) => {
  if (!coverage) return null;

  const sections = coverage.sections || [];
  const byCompany = Boolean(coverage.grouped_by_company);
  const subtitle =
    coverage.scope === 'team'
      ? 'You and your inspectors'
      : sections.length === 1
        ? sections[0].company_name
        : null;

  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className="px-5 py-4 border-b border-gray-700">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-white">
            {COVERAGE_TITLES[coverage.scope] || 'Coverage'}
          </h2>
          {subtitle && <span className="text-xs text-gray-500 truncate">{subtitle}</span>}
        </div>
        {byCompany && sections.length > 1 && (
          <p className="mt-1 text-xs text-gray-500">
            {coverage.submitted_count} of {coverage.total} submitted across{' '}
            {plural(sections.length, 'company', 'companies')}
          </p>
        )}
      </div>

      {sections.length === 0 ? (
        <p className="px-5 py-6 text-sm text-gray-500">No accounts in scope.</p>
      ) : (
        sections.map((section) => (
          <CoverageSection
            key={section.company_id ?? 'none'}
            section={section}
            collapsible={byCompany}
            isOpen={isOpen}
            toggle={toggle}
          />
        ))
      )}
    </div>
  );
};

const FormSubmissionsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const canManage = canManageForms(user.role);
  const isDev = user.role === 'dev';

  useEffect(() => {
    // The screen is reached from a button only managers and above can see; a
    // hand-typed URL lands back on the forms list rather than a failed fetch.
    if (!canManage) navigate('/forms', { replace: true });
  }, [canManage, navigate]);

  useEffect(() => {
    if (!canManage) return undefined;
    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [formRes, submissionsRes, coverageRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/forms/${id}`),
          axios.get(`${API_BASE_URL}/api/forms/${id}/submissions`),
          axios.get(`${API_BASE_URL}/api/forms/${id}/submission-coverage`),
        ]);
        if (cancelled) return;
        setForm(formRes.data);
        setSubmissions(submissionsRes.data);
        setCoverage(coverageRes.data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load form submissions:', err);
        setError(
          err.response?.status === 404
            ? 'Form not found.'
            : err.response?.status === 403
              ? 'You do not have permission to view submissions for this form.'
              : 'Failed to load submissions.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [id, canManage]);

  const userGroups = useMemo(() => groupSubmissionsByUser(submissions), [submissions]);
  const companyGroups = useMemo(
    () => (isDev ? groupSubmissionsByCompany(submissions) : []),
    [submissions, isDev]
  );

  // Sections remember only what the viewer changed; anything untouched falls
  // back to the default for its level (top level open, everything below shut).
  const isOpen = useCallback((key, defaultOpen) => overrides[key] ?? defaultOpen, [overrides]);
  const toggle = useCallback((key, defaultOpen) => {
    setOverrides((prev) => ({ ...prev, [key]: !(prev[key] ?? defaultOpen) }));
  }, []);
  const openSubmission = useCallback(
    (submissionId) => navigate(`/submitted/${submissionId}`),
    [navigate]
  );

  if (!canManage) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-300">Loading submissions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="text-red-300 mb-4">{error}</div>
          <button onClick={() => navigate('/forms')} className={PRIMARY_BTN}>
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/forms')}
            className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-gray-600 hover:border-gray-500"
          >
            ← Back to Forms
          </button>
        </div>

        <div className="md:flex md:items-center md:justify-between mb-8 gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold text-white mb-2 truncate">
              {form?.name || `Form #${id}`}
            </h1>
            <p className="text-lg text-gray-400">
              {submissions.length === 0
                ? 'No submissions yet.'
                : `${plural(submissions.length, 'submission')} from ${plural(
                    userGroups.length,
                    'person',
                    'people'
                  )}.`}
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex-shrink-0">
            <button onClick={() => navigate(`/forms/${id}/fill`)} className={PRIMARY_BTN}>
              Start New Submission
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Submissions, nested by company (devs only), submitter, then re-submission */}
          <div className={`${CARD} overflow-hidden lg:col-span-2`}>
            {userGroups.length === 0 ? (
              <div className="text-center py-16 px-4">
                <FlameIcon className="mx-auto w-16 h-16 mb-6" />
                <h3 className="text-xl font-semibold text-white mb-2">No submissions yet</h3>
                <p className="text-gray-400 max-w-sm mx-auto">
                  Nobody has submitted this survey yet. Submissions will appear here grouped by
                  who sent them.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-700">
                {isDev
                  ? companyGroups.map((company) => (
                      <CompanySection
                        key={company.key}
                        company={company}
                        isOpen={isOpen}
                        toggle={toggle}
                        onOpen={openSubmission}
                      />
                    ))
                  : userGroups.map((group) => (
                      <UserSection
                        key={group.key}
                        group={group}
                        depth={0}
                        isOpen={isOpen}
                        toggle={toggle}
                        onOpen={openSubmission}
                      />
                    ))}
              </ul>
            )}
          </div>

          {/* Who has and hasn't submitted, over the same people as the list */}
          <CoveragePanel coverage={coverage} isOpen={isOpen} toggle={toggle} />
        </div>
      </div>
    </div>
  );
};

export default FormSubmissionsPage;
